import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppSettingsProvider } from "./context/AppSettingsContext";
import { ThemeProvider } from "./context/ThemeContext";
import { useNotifications } from "./hooks/useNotifications";
import Navbar from "./components/Navbar";
import BottomNav from "./components/BottomNav";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifyId from "./pages/VerifyId";
import Browse from "./pages/Browse";
import Sell from "./pages/Sell";
import ListingDetail from "./pages/ListingDetail";
import Swap from "./pages/Swap";
import Notes from "./pages/Notes";
import LostFound from "./pages/LostFound";
import Rideshare from "./pages/Rideshare";
import Events from "./pages/Events";
import Tutoring from "./pages/Tutoring";
import Requests from "./pages/Requests";
import Giveaway from "./pages/Giveaway";
import NoticeBoard from "./pages/NoticeBoard";
import Profile from "./pages/Profile";
import MyListings from "./pages/MyListings";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import ParentZone from "./pages/ParentZone";
import Landing from "./pages/Landing";
import Chat from "./pages/Chat";
import BookExchange from "./pages/BookExchange";
import ExamPrepZone from "./pages/ExamPrepZone";
import Ambassador from "./pages/Ambassador";
import Onboarding from "./pages/Onboarding";
import Notifications from "./pages/Notifications";
import Saved from "./pages/Saved";
import SellerProfile from "./pages/SellerProfile";
import Referrals from "./pages/Referrals";
import TransactionHistory from "./pages/TransactionHistory";
import RentalBookings from "./pages/RentalBookings";
import Leaderboard from "./pages/Leaderboard";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import ContactUs from "./pages/ContactUs";
import InstallBanner from "./components/InstallBanner";
import InAppToast from "./components/InAppToast";
import "./index.css";

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error("App error:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px", background: "#f8fafc", textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>😕</div>
          <h2 style={{ fontWeight: 700, fontSize: 20, color: "#1e293b", marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ color: "#64748b", fontSize: 14, marginBottom: 24 }}>Please refresh the page. If the problem continues, contact support.</p>
          <button onClick={() => window.location.reload()} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 12, padding: "12px 28px", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function PrivateRoute({ children }) {
  const { currentUser, userProfile, isAdmin } = useAuth();
  if (!currentUser) return <Navigate to="/about" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { currentUser } = useAuth();
  return !currentUser ? children : <Navigate to="/" replace />;
}

function LandingRoute() {
  const { currentUser } = useAuth();
  return currentUser ? <Navigate to="/" replace /> : <Landing />;
}

function AppContent() {
  const { currentUser } = useAuth();
  useNotifications();
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith("/admin");
  const noNavPaths = ["/login", "/signup", "/about", "/privacy-policy", "/terms", "/ambassador", "/contact", "/onboarding"];
  const isListingPage = location.pathname.startsWith("/listing/");
  const hideNav = isAdminPage || noNavPaths.includes(location.pathname);
  return (
    <>
      {currentUser && !hideNav && <Navbar />}
      <main className="max-w-lg mx-auto">
        <Routes>
          <Route path="/about" element={<LandingRoute />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/" element={<PrivateRoute><Home /></PrivateRoute>} />
          <Route path="/verify-id" element={<PrivateRoute><VerifyId /></PrivateRoute>} />
          <Route path="/browse" element={<PrivateRoute><Browse /></PrivateRoute>} />
          <Route path="/sell" element={<PrivateRoute><Sell /></PrivateRoute>} />
          <Route path="/listing/:id" element={<ListingDetail />} />
          <Route path="/swap" element={<PrivateRoute><Swap /></PrivateRoute>} />
          <Route path="/notes" element={<PrivateRoute><Notes /></PrivateRoute>} />
          <Route path="/lost-found" element={<PrivateRoute><LostFound /></PrivateRoute>} />
          <Route path="/rideshare" element={<PrivateRoute><Rideshare /></PrivateRoute>} />
          <Route path="/events" element={<PrivateRoute><Events /></PrivateRoute>} />
          <Route path="/tutoring" element={<PrivateRoute><Tutoring /></PrivateRoute>} />
          <Route path="/requests" element={<PrivateRoute><Requests /></PrivateRoute>} />
          <Route path="/giveaway" element={<PrivateRoute><Giveaway /></PrivateRoute>} />
          <Route path="/notice-board" element={<PrivateRoute><NoticeBoard /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/my-listings" element={<PrivateRoute><MyListings /></PrivateRoute>} />
          <Route path="/parent-zone" element={<PrivateRoute><ParentZone /></PrivateRoute>} />
          <Route path="/chat" element={<PrivateRoute><Chat /></PrivateRoute>} />
          <Route path="/chat/:chatId" element={<PrivateRoute><Chat /></PrivateRoute>} />
          <Route path="/book-exchange" element={<PrivateRoute><BookExchange /></PrivateRoute>} />
          <Route path="/exam-prep" element={<PrivateRoute><ExamPrepZone /></PrivateRoute>} />
          <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
          <Route path="/saved" element={<PrivateRoute><Saved /></PrivateRoute>} />
          <Route path="/seller/:uid" element={<PrivateRoute><SellerProfile /></PrivateRoute>} />
          <Route path="/referrals" element={<PrivateRoute><Referrals /></PrivateRoute>} />
          <Route path="/transactions" element={<PrivateRoute><TransactionHistory /></PrivateRoute>} />
          <Route path="/rental-bookings" element={<PrivateRoute><RentalBookings /></PrivateRoute>} />
          <Route path="/leaderboard" element={<PrivateRoute><Leaderboard /></PrivateRoute>} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/ambassador" element={<Ambassador />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {currentUser && !hideNav && <BottomNav />}
      <InstallBanner />
      {currentUser && <InAppToast />}
    </>
  );
}

function AppRoutes() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AppSettingsProvider>
            <AppRoutes />
          </AppSettingsProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
